import type { z } from 'zod';
import type { AppFormValues } from '@/features/dashboard/components/AppForm/AppForm/context/types';
import type { MarketApp, TilesWidgetV2, WidgetV2 } from '@/features/dashboard/types';
import { AppType } from '@/features/dashboard/types';

import { basicTableDefinition } from './definitions/basicTable';
import { fwdChartDefinition } from './definitions/fwdChart';
import { highlightedPriceDefinition } from './definitions/highlightedPrice';
import { seasonalChartDefinition } from './definitions/seasonalChart';
import { spotChartDefinition } from './definitions/spotChart';
import { spotHistoryTableDefinition } from './definitions/spotHistoryTable';
import type { FormAppDefinition, FormAppType, FormAppWidget } from './types';

type RuntimeFormAppDefinition = Omit<
  FormAppDefinition<AppFormValues, FormAppWidget>,
  'schema' | 'toWidget' | 'canEditWidget' | 'fromWidget'
> & {
  schema: z.ZodType<AppFormValues>;
  toWidget: (values: AppFormValues) => MarketApp;
  canEditWidget: (widget: FormAppWidget) => boolean;
  fromWidget: (widget: FormAppWidget) => AppFormValues;
};

function toRuntimeDefinition<TValues extends AppFormValues, TWidget extends FormAppWidget>(
  definition: FormAppDefinition<TValues, TWidget>
): RuntimeFormAppDefinition {
  return {
    ...definition,
    schema: definition.schema as z.ZodType<AppFormValues>,
    toWidget: values => definition.toWidget(values as TValues),
    canEditWidget: widget => definition.canEditWidget(widget),
    fromWidget: widget => definition.fromWidget(widget as TWidget)
  };
}

export const FORM_APP_DEFINITIONS = {
  [AppType.SpotHistoryTable]: toRuntimeDefinition(spotHistoryTableDefinition),
  [AppType.Table]: toRuntimeDefinition(basicTableDefinition),
  [AppType.Tiles]: toRuntimeDefinition(highlightedPriceDefinition),
  [AppType.Spot]: toRuntimeDefinition(spotChartDefinition),
  [AppType.Seasonal]: toRuntimeDefinition(seasonalChartDefinition),
  [AppType.FWD]: toRuntimeDefinition(fwdChartDefinition)
} satisfies Record<FormAppType, RuntimeFormAppDefinition>;

export function getFormAppDefinition(type: FormAppType): RuntimeFormAppDefinition {
  const definition = FORM_APP_DEFINITIONS[type];
  if (!definition) {
    throw new Error(`Missing dashboard app definition for ${type}`);
  }
  return definition;
}

export function getDefinitionForWidget(widget: WidgetV2 | TilesWidgetV2): RuntimeFormAppDefinition {
  const definition = Object.values(FORM_APP_DEFINITIONS).find(def => def.canEditWidget(widget));
  if (!definition) {
    throw new Error(`Unsupported editable dashboard widget type: ${widget.type}`);
  }
  return definition;
}

export function canEditWidgetInDashboard(widget: WidgetV2 | TilesWidgetV2): boolean {
  return Object.values(FORM_APP_DEFINITIONS).some(
    definition => Boolean(definition.dashboardControls?.edit) && definition.canEditWidget(widget)
  );
}

export function canExportWidget(widget: WidgetV2 | TilesWidgetV2): boolean {
  return Object.values(FORM_APP_DEFINITIONS).some(
    definition => Boolean(definition.widgetControls?.export) && definition.canEditWidget(widget)
  );
}
